<script setup>
import { useStore } from "vuex";
import { useLoading } from "vue-loading-overlay";
import { computed, onMounted, ref, watch } from "vue";
import moment from "moment";
import { errorNotify, successNotify } from "@/mixins";
import DataTable from "@/components/panels/DataTable.vue";
import RowActions from "@/components/panels/RowActions.vue";
import FormModal from "@/components/modals/FormModal.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import PaginationBar from "@/components/layouts/PaginationBar.vue";

const store = useStore();
const $loading = useLoading({});

const headFirmwareList = computed(() => store.getters['firmware/headFirmwareList']);
const cellFirmwareList = computed(() => store.getters['firmware/cellFirmwareList']);

// two boards: mainboard (head) and cellboard (cell) — a single table with a board chip
const BOARD_META = {
  head: { cls: 'fw-main', icon: 'chip', label: 'Mainboard' },
  cell: { cls: 'fw-cell', icon: 'chip', label: 'Cellboard' },
};

const search = ref('');
const boardFilter = ref('');
const PAGE_SIZE = 10;
const page = ref(1);

const allRows = computed(() => {
  const heads = (headFirmwareList.value?.results || []).map((f) => ({ ...f, board: 'head' }));
  const cells = (cellFirmwareList.value?.results || []).map((f) => ({ ...f, board: 'cell' }));
  return [...heads, ...cells].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
});

const filteredRows = computed(() => allRows.value.filter((f) => {
  const q = search.value.trim().toLowerCase();
  const matchesQuery = !q
    || String(f.version).toLowerCase().includes(q)
    || (f.description || '').toLowerCase().includes(q)
    || (f.file || '').toLowerCase().includes(q);
  return matchesQuery && (!boardFilter.value || f.board === boardFilter.value);
}));

const totalPages = computed(() => Math.max(1, Math.ceil(filteredRows.value.length / PAGE_SIZE)));
const pagedRows = computed(() =>
  filteredRows.value.slice((page.value - 1) * PAGE_SIZE, page.value * PAGE_SIZE));

watch([search, boardFilter], () => { page.value = 1; });

const latestOf = (list) => {
  const results = list?.results || [];
  return results.length ? results[0].version : '—';
};

function loadAll() {
  const loader = $loading.show({});
  return Promise.all([
    store.dispatch('firmware/getHeadFirmwareList', '?page_size=1000'),
    store.dispatch('firmware/getCellFirmwareList', '?page_size=1000'),
  ]).finally(() => loader.hide());
}

onMounted(loadAll);

const fmt = (ts) => (ts ? moment(ts).format('MMM D, YYYY') : '—');
const fileName = (url) => (url ? decodeURIComponent(String(url).split('/').pop().split('?')[0]) : '');

const metrics = computed(() => [
  { label: 'Mainboard', value: headFirmwareList.value?.count ?? 0, icon: 'chip', color: 'var(--lb-blue)' },
  { label: 'Latest main', value: latestOf(headFirmwareList.value), icon: 'tag-outline', color: 'var(--lb-blue)' },
  { label: 'Cellboard', value: cellFirmwareList.value?.count ?? 0, icon: 'chip', color: 'var(--lb-warning)' },
  { label: 'Latest cell', value: latestOf(cellFirmwareList.value), icon: 'tag-outline', color: 'var(--lb-warning)' },
]);

const totalCount = computed(() =>
  (headFirmwareList.value?.count ?? 0) + (cellFirmwareList.value?.count ?? 0));

// allRows is sorted newest-first, so the first entry is the most recent
// upload across both boards — real data, no demo needed here.
const latestUpload = computed(() => allRows.value[0] || null);

const boardData = computed(() => [
  { label: 'Mainboard', v: headFirmwareList.value?.count ?? 0, color: 'var(--lb-blue)' },
  { label: 'Cellboard', v: cellFirmwareList.value?.count ?? 0, color: 'var(--lb-warning)' },
]);
const boardDataMax = computed(() => Math.max(...boardData.value.map((d) => d.v), 1));

// ---- create ----
// Firmwares are immutable — upload only, there is no edit flow.
const modal = ref(false);
const submitting = ref(false);

const fwFields = [
  {
    key: 'board', label: 'Board', type: 'select', required: true, full: true, placeholder: 'Select board',
    options: [
      { value: 'head', label: 'Mainboard (head unit)' },
      { value: 'cell', label: 'Cellboard' },
    ],
  },
  { key: 'name', label: 'Name', placeholder: 'stable', required: true },
  { key: 'version', label: 'Version', placeholder: '2.4.1', required: true },
  { key: 'description', label: 'Description', type: 'textarea', optional: true, full: true },
  { key: 'file', label: 'Firmware file', type: 'file', required: true, full: true, placeholder: 'Choose a firmware binary…' },
];

function openModal() {
  modal.value = true;
}

function saveFirmware(vals) {
  submitting.value = true;
  const formData = new FormData();
  formData.append('name', vals.name);
  formData.append('version', vals.version);
  formData.append('description', vals.description || '');
  if (vals.file instanceof File) formData.append('file', vals.file);
  store.dispatch('firmware/createFirmware', { firmwareType: vals.board, data: formData })
    .then(() => {
      const err = store.getters['firmware/createFirmwareError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify('Firmware uploaded');
        modal.value = false;
        loadAll();
      }
    })
    .finally(() => { submitting.value = false; });
}

// ---- delete ----
const del = ref(null);
function confirmDelete() {
  store.dispatch('firmware/deleteFirmware', { firmwareType: del.value.board, id: del.value.id })
    .then(() => {
      const err = store.getters['firmware/deleteFirmwareError'];
      if (err) errorNotify(err);
      else successNotify('Firmware deleted');
      del.value = null;
      loadAll();
    });
}

const cols = '110px 130px minmax(220px,1.6fr) minmax(180px,1fr) 130px';
const columns = ['Version', 'Board', 'Description', 'File', 'Created'];
</script>

<template>
  <Teleport to="#topbar-actions">
    <button class="btn-primary" type="button" @click="openModal">
      <span class="mdi mdi-plus"></span>
      Upload Firmware
    </button>
  </Teleport>

  <!-- metrics -->
  <div class="ro-top">
    <div class="metric-cluster">
      <div class="mc-total">
        <span class="mc-total__wm mdi mdi-chip"></span>
        <div class="mc-total__title">Firmware versions</div>
        <div class="mc-total__num">{{ totalCount }}</div>
      </div>
      <div v-for="m in metrics" :key="m.label" class="mcard">
        <div class="mcard__l">
          <div class="mcard__ico"><span class="mdi" :class="`mdi-${m.icon}`" :style="{ color: m.color }"></span></div>
          <div class="mcard__label">{{ m.label }}</div>
        </div>
        <div class="mcard__num" style="font-size: 20px;">{{ m.value }}</div>
      </div>
    </div>

    <div class="ro-right">
      <div class="ro-card avgcard">
        <span class="avgcard__wm mdi mdi-cloud-upload-outline"></span>
        <div class="avgcard__title">Latest upload</div>
        <div class="avgcard__num">{{ latestUpload ? `v${latestUpload.version}` : '—' }}</div>
        <div class="avgcard__foot">
          {{ latestUpload ? `${BOARD_META[latestUpload.board].label} · ${fmt(latestUpload.created_at)}` : 'No firmware uploaded yet' }}
        </div>
      </div>
      <div class="ro-card">
        <div class="ro-card__head">
          <span class="mdi mdi-chart-bar"></span>
          <h3>Versions by Board</h3>
          <span class="sub">{{ totalCount }} total</span>
        </div>
        <div class="weekchart">
          <div v-for="d in boardData" :key="d.label" class="weekbar">
            <div class="weekbar__val">{{ d.v }}</div>
            <div class="weekbar__track">
              <div class="weekbar__fill" :style="{ height: `${(d.v / boardDataMax) * 100}%`, background: d.color }"></div>
            </div>
            <div class="weekbar__day">{{ d.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- toolbar -->
  <div class="toolbar">
    <div class="field-search">
      <span class="mdi mdi-magnify"></span>
      <input v-model="search" placeholder="Search version, file or notes…">
    </div>
    <div class="field-select">
      <span class="mdi mdi-chip lead"></span>
      <select v-model="boardFilter">
        <option value="">All boards</option>
        <option value="head">Mainboard</option>
        <option value="cell">Cellboard</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="spacer"></div>
    <span class="count">{{ filteredRows.length }} of {{ totalCount }} firmwares</span>
  </div>

  <DataTable :cols="cols" :columns="columns" :rows="pagedRows" empty-text="No firmwares match your filters.">
    <template #row="{ row }">
      <div class="td"><span class="verpill">v{{ row.version }}</span></div>
      <div class="td">
        <span class="inputtype" :class="BOARD_META[row.board].cls">
          <span class="mdi" :class="`mdi-${BOARD_META[row.board].icon}`"></span>
          {{ BOARD_META[row.board].label }}
        </span>
      </div>
      <div class="td"><span class="muted">{{ row.description || '—' }}</span></div>
      <div class="td">
        <a v-if="row.file" class="filechip" :href="row.file" target="_blank" rel="noopener" @click.stop>
          <span class="mdi mdi-file-download-outline"></span>
          {{ fileName(row.file) }}
        </a>
        <span v-else class="muted">—</span>
      </div>
      <div class="td"><span class="muted">{{ fmt(row.created_at) }}</span></div>
      <RowActions
        :copies="[{ icon: 'content-copy', label: 'Copy version', text: row.version }]"
        deletable
        @delete="del = row" />
    </template>
  </DataTable>

  <PaginationBar
    :page="page"
    :total-pages="totalPages"
    :count="filteredRows.length"
    @update:page="(p) => { page = p; }" />

  <FormModal
    :open="modal"
    title="Upload Firmware"
    :fields="fwFields"
    submit-label="Upload"
    :submitting="submitting"
    @submit="saveFirmware"
    @cancel="modal = false" />

  <ConfirmModal
    :open="!!del"
    danger
    title="Delete firmware?"
    :message="del ? `“${del.name} v${del.version}” will be permanently removed.` : ''"
    confirm-label="Delete"
    @confirm="confirmDelete"
    @cancel="del = null" />
</template>
